/**
 * @file Per-shot damage bookkeeping for multi-pellet weapons (GDD §8).
 *
 * A shotgun blast is nine independent projectiles that must still read as ONE
 * attack: nine pellets buried in the same grunt may not deal nine times the
 * damage. Every pellet of a trigger pull therefore shares a single shot record,
 * and the collision system asks that record how much of a pellet's damage the
 * target is still allowed to take.
 *
 * The record travels with the pellets rather than living in a registry keyed by
 * shot id, so it dies with the last pellet and no system has to reach into
 * another system's state.
 */

/** @type {number} Monotonic id, useful only when debugging a single blast. */
let nextShotId = 1;

/**
 * @typedef {object} ShotRecord
 * @property {number} id Unique id of the trigger pull.
 * @property {number} maxDamagePerTarget Damage ceiling per target, per shot.
 * @property {Map<number, number>} dealt Damage booked so far, by entity id.
 */

/**
 * Opens the ledger for one trigger pull.
 *
 * @param {number} [maxDamagePerTarget] Damage ceiling per target; `Infinity`
 *   (the default) leaves single-projectile weapons completely unaffected.
 * @returns {ShotRecord}
 */
export function createShot(maxDamagePerTarget = Infinity) {
  return { id: nextShotId++, maxDamagePerTarget, dealt: new Map() };
}

/**
 * Damage multiplier for a projectile that has flown `travelled` of its `range`.
 * Full damage up to `start` of the range, then a straight line down to `min` at
 * the very edge — simple enough to reason about while tuning.
 *
 * @param {number} travelled Distance flown so far, in world units.
 * @param {number} range Maximum travel distance of the projectile.
 * @param {number} start Fraction of the range where the drop-off begins, 0..1.
 * @param {number} min Multiplier at maximum range, 0..1.
 * @returns {number} Multiplier in `[min, 1]`.
 */
export function falloffScale(travelled, range, start, min) {
  if (min >= 1 || range <= 0 || start >= 1) return 1;

  const distance = Math.max(0, Math.min(1, travelled / range));
  if (distance <= start) return 1;

  const t = (distance - start) / (1 - start);
  return 1 + (min - 1) * t;
}

/**
 * Books damage against one target and reports how much of it the target may
 * actually take. Pellets past the ceiling are refused rather than trimmed to
 * zero silently, so the caller can skip the damage event entirely.
 *
 * @param {ShotRecord | null} shot Ledger shared by every projectile of the shot.
 * @param {number} targetId Entity id that was hit.
 * @param {number} amount Damage the projectile wants to deal.
 * @returns {number} Damage granted, `0` when the target is already saturated.
 */
export function claimDamage(shot, targetId, amount) {
  if (!shot) return amount;

  const dealt = shot.dealt.get(targetId) ?? 0;
  const granted = Math.min(amount, shot.maxDamagePerTarget - dealt);
  if (granted <= 0) return 0;

  shot.dealt.set(targetId, dealt + granted);
  return granted;
}
